import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CuriosityCelebration from './CuriosityCelebration';
import CelebrationAnimation from './CelebrationAnimation';

/**
 * GlobalCelebration - Complete implementation with instant close support
 * 
 * Key features:
 * - Animation lock prevents duplicate triggering
 * - Portal-based rendering prevents parent re-renders from affecting animation
 * - Instant close support via 'close-celebration' event
 * - Proper cleanup of all animations and timers
 */
export default function GlobalCelebration() {
  const [config, setConfig] = useState(null); // { type, points, gems }
  const isAnimatingRef = useRef(false); // Prevent duplicate triggering
  const forceCloseRef = useRef(false); // Track if celebration should be force-closed
  const celebrationRef = useRef(null); // Ref to celebration component for cleanup

  useEffect(() => {
    const onCompleteEvent = (event) => {
      const detail = event.detail;
      
      // Skip if already completed or no detail
      if (!detail || detail.isAlreadyCompleted) {
        return;
      }

      // Extract and normalize activity type
      const rawType = detail.activityType;
      if (!rawType) {
        console.warn('⚠️ No activity type in event, skipping celebration');
        return;
      }

      // Normalize type
      const normalizedType = rawType.charAt(0).toUpperCase() + rawType.slice(1).toLowerCase();

      // Prevent duplicate animations
      if (isAnimatingRef.current) {
        console.warn('⚠️ Celebration blocked: already animating');
        return;
      }

      // Reset force close flag
      forceCloseRef.current = false;

      // Lock animation and set config
      isAnimatingRef.current = true;
      setConfig({
        type: normalizedType,
        points: detail.pointsEarned || 0,
        gems: detail.gemsEarned || 0,
      });
    };

    // Listen for close-celebration event to immediately kill animations
    const forceClose = () => {
      console.log('🛑 Force closing celebration...');
      // Set force close flag FIRST
      forceCloseRef.current = true;
      // Call forceClose on celebration component if it exists (before unmounting)
      if (celebrationRef.current && typeof celebrationRef.current.forceClose === 'function') {
        celebrationRef.current.forceClose();
      }
      // Immediately kill all animations and reset state
      isAnimatingRef.current = false;
      // Use setTimeout to ensure ref cleanup happens before state update
      setTimeout(() => {
        setConfig(null);
      }, 0);
    };

    window.addEventListener('activity-completed', onCompleteEvent);
    window.addEventListener('close-celebration', forceClose);
    
    return () => {
      window.removeEventListener('activity-completed', onCompleteEvent);
      window.removeEventListener('close-celebration', forceClose);
    };
  }, []);

  const handleAnimationComplete = () => {
    // Only complete if not force-closed
    if (forceCloseRef.current) {
      return;
    }
    // Delay state reset to allow fade-out animation
    setTimeout(() => {
      if (!forceCloseRef.current) {
        setConfig(null);
        isAnimatingRef.current = false;
      }
    }, 300);
  };

  // Don't render if no config
  if (!config) {
    return null;
  }

  // Select component based on type
  const Component = config.type === 'Curiosity' ? CuriosityCelebration : CelebrationAnimation;

  // Render in portal to prevent parent re-renders from affecting animation
  // Pass forceClose prop so component can check it
  return createPortal(
    <Component
      ref={celebrationRef}
      points={config.points}
      gems={config.gems}
      onComplete={handleAnimationComplete}
      forceClose={forceCloseRef.current}
    />,
    document.body
  );
}
